import React from "react";
import api, { postForm } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useTerminology } from "../../context/TerminologyContext";
import PinModal, { usePinGate } from "../../components/PinModal";
import PageHeader from "../../components/PageHeader";
import { formatDateTime } from "../../utils/datetime";
import { DateRangePicker, ModalShell, Pager, usePagination } from "../../components/ui-helpers";
import {
  MaterialTemplateModal,
  useMaterialTemplateModal,
} from "../../components/MaterialTemplateModal";

function vendorStatusMeta(value) {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (raw === "1" || raw === "true" || raw === "yes" || raw === "active")
    return {
      approved: true,
      status: "approved",
      label: "Approved",
      cls: "status-approved",
    };
  if (raw === "rejected")
    return {
      approved: false,
      status: "rejected",
      label: "Suspended",
      cls: "status-rejected",
    };
  if (raw === "deleted")
    return {
      approved: false,
      status: "deleted",
      label: "Removed",
      cls: "status-rejected",
    };
  return {
    approved: false,
    status: "pending",
    label: "Not Approved",
    cls: "status-pending",
  };
}

function materialStatusMeta(value, approvedValue) {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (
    raw === "approved" ||
    raw === "1" ||
    raw === "true" ||
    raw === "yes" ||
    raw === "active"
  )
    return {
      approved: true,
      status: "approved",
      label: "Approved",
      cls: "status-approved",
    };
  if (raw === "rejected")
    return {
      approved: false,
      status: "rejected",
      label: "Rejected",
      cls: "status-rejected",
    };
  if (raw === "sent_back")
    return {
      approved: false,
      status: "sent_back",
      label: "Reverted",
      cls: "status-pending",
    };
  if (raw === "withdrawn")
    return {
      approved: false,
      status: "withdrawn",
      label: "Withdrawn",
      cls: "status-rejected",
    };
  if (raw === "pending")
    return {
      approved: false,
      status: "pending",
      label: "Pending",
      cls: "status-pending",
    };
  const fallback = String(approvedValue ?? "")
    .trim()
    .toLowerCase();
  if (["1", "true", "yes", "active"].includes(fallback))
    return {
      approved: true,
      status: "approved",
      label: "Approved",
      cls: "status-approved",
    };
  return {
    approved: false,
    status: "pending",
    label: "Pending",
    cls: "status-pending",
  };
}

const MATERIAL_STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "sent_back", label: "Reverted" },
  { value: "withdrawn", label: "Withdrawn" },
];

function isLocalDateInRange(value, range) {
  const raw = String(value || "").slice(0, 10);
  if (!raw) return true;
  if (range?.date_from && raw < range.date_from) return false;
  if (range?.date_to && raw > range.date_to) return false;
  return true;
}

export default function ReviewVendorsMaterialsPage() {
  const { user } = useAuth();
  const { t, tp } = useTerminology();
  const [vendors, setVendors] = React.useState([]);
  const [byVendor, setByVendor] = React.useState({});
  const [selectedVendor, setSelectedVendor] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("vendors");
  const [vendorSearch, setVendorSearch] = React.useState("");
  const [materialSearch, setMaterialSearch] = React.useState("");
  const [materialStatusFilter, setMaterialStatusFilter] = React.useState("");
  const [materialDateDraft, setMaterialDateDraft] = React.useState({ date_from: "", date_to: "" });
  const [materialDateRange, setMaterialDateRange] = React.useState({ date_from: "", date_to: "" });
  const [vendorSort, setVendorSort] = React.useState("name");
  const [materialSort, setMaterialSort] = React.useState("material");
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const pinGate = usePinGate();
  const templateModal = useMaterialTemplateModal();
  const [materialModal, setMaterialModal] = React.useState({ open: false, action: "", item: null, comments: "" });
  const [vendorModal, setVendorModal] = React.useState({ open: false, item: null, comments: "" });

  const load = React.useCallback(async () => {
    try {
      const response = await api.get("/manager/list_vendor_materials");
      const vendorRows = response.data?.vendors || [];
      const nextByVendor = response.data?.materials_by_vendor || {};
      setVendors(vendorRows);
      setByVendor(nextByVendor);
      if (!selectedVendor && vendorRows[0]?.name)
        setSelectedVendor(vendorRows[0].name);
      if (
        selectedVendor &&
        !vendorRows.some((item) => item.name === selectedVendor)
      ) {
        setSelectedVendor(vendorRows[0]?.name || "");
      }
    } catch {
      setVendors([]);
      setByVendor({});
    }
  }, [selectedVendor]);

  React.useEffect(() => {
    load().catch(() => {});
  }, [load]);


  const runVendorDecision = async (name, approved, comments = "") => {
    setMessage("");
    setError("");
    try {
      const response = await postForm("/manager/approve_vendor", {
        vendor_name: name,
        approved: approved ? "1" : "0",
        manager_id: user?.uid || "manager",
        comments,
      });
      await load();
      setMessage(response.data?.message || `${t("vendor")} ${approved ? "approved" : "suspended"}.`);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          `Unable to update ${t("vendor").toLowerCase()} approval.`,
      );
    }
  };

  const materialIdentityOf = (item) =>
    item?.material_id || item?.Material_ID || item?.id || "";

  const materialRowKey = (vendor, item, index) => {
    const materialId = materialIdentityOf(item);
    if (materialId) return `${vendor}-material-${materialId}`;
    return `${vendor}-${item?.material || "material"}-${item?.sku || "sku"}-${item?.created_at || index}`;
  };

  const runMaterialDecision = async (item, action, comments = "") => {
    setMessage("");
    setError("");
    try {
      const materialId = materialIdentityOf(item);
      const response = await postForm("/manager/material_decision", {
        vendor_name: item?.vendor || "",
        sku: item?.sku || "",
        material_id: materialId || undefined,
        action,
        manager_id: user?.uid || "manager",
        comments,
      });
      await load();
      setMessage(response.data?.message || `${t("material")} status updated.`);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          `Unable to update ${t("material").toLowerCase()} status.`,
      );
    }
  };

  const openMaterialCommentModal = (item, action) => {
    setMaterialModal({ open: true, action, item, comments: "" });
  };

  const submitMaterialCommentModal = () => {
    const { item, action, comments } = materialModal;
    setMaterialModal({ open: false, action: "", item: null, comments: "" });
    pinGate.askPinThen(() => runMaterialDecision(item, action, comments));
  };

  const openVendorSuspendModal = (item) => {
    setVendorModal({ open: true, item, comments: "" });
  };

  const submitVendorCommentModal = () => {
    const { item, comments } = vendorModal;
    setVendorModal({ open: false, item: null, comments: "" });
    pinGate.askPinThen(() => runVendorDecision(item?.name || item?.vendor || "", false, comments));
  };

  const materialCommentActionLabel = (action) => {
    if (action === "withdraw") return "Withdraw";
    if (action === "reject") return "Reject";
    return "Revert";
  };

  const visibleVendors = React.useMemo(() => {
    const needle = vendorSearch.trim().toLowerCase();
    const rows = (vendors || []).filter(
      (item) =>
        !needle ||
        `${item.name || ""} ${item.email || ""} ${item.status || item.approved || ""} ${item.last_decision_comment || ""}`
          .toLowerCase()
          .includes(needle),
    );
    rows.sort((a, b) => {
      if (vendorSort === "status")
        return String(a.status || a.approved || "").localeCompare(
          String(b.status || b.approved || ""),
        );
      if (vendorSort === "created")
        return String(a.created_at || "").localeCompare(
          String(b.created_at || ""),
        );
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
    return rows;
  }, [vendors, vendorSearch, vendorSort]);

  const vendorPager = usePagination(visibleVendors, 10);

  const materialRows = React.useMemo(() => {
    const needle = materialSearch.trim().toLowerCase();
    const rows = [];
    Object.entries(byVendor || {}).forEach(([vendor, items]) => {
      (items || []).forEach((item) =>
        rows.push({ ...item, vendor: item.vendor || vendor }),
      );
    });
    const filtered = rows.filter((item) => {
      const state = materialStatusMeta(item.approval_status, item.approved);
      if (materialStatusFilter && state.status !== materialStatusFilter) return false;
      if (!isLocalDateInRange(item.created_at, materialDateRange)) return false;
      if (!needle) return true;
      return `${item.vendor || ""} ${item.material || ""} ${item.sku || ""} ${item.type || ""} ${item.std_mrp_rs || ""} ${state.label || ""} ${item.approval_status || ""} ${item.created_at || ""} ${item.last_decision_comment || ""}`
        .toLowerCase()
        .includes(needle);
    });
    filtered.sort((a, b) => {
      if (materialSort === "vendor")
        return String(a.vendor || "").localeCompare(String(b.vendor || ""));
      if (materialSort === "sku")
        return String(a.sku || "").localeCompare(String(b.sku || ""));
      if (materialSort === "type")
        return String(a.type || "").localeCompare(String(b.type || ""));
      if (materialSort === "status")
        return materialStatusMeta(
          a.approval_status,
          a.approved,
        ).label.localeCompare(
          materialStatusMeta(b.approval_status, b.approved).label,
        );
      if (materialSort === "created")
        return String(a.created_at || "").localeCompare(
          String(b.created_at || ""),
        );
      return String(a.material || "").localeCompare(String(b.material || ""));
    });
    return filtered;
  }, [byVendor, materialSearch, materialStatusFilter, materialDateRange, materialSort]);

  const materialPager = usePagination(materialRows, 10);

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Manager"
        icon="review"
        title={`Review ${tp("vendor")} & ${tp("material")}`}
        subtitle={`Search pending onboarding items, review ${t("vendor").toLowerCase()} and ${t("material").toLowerCase()} tables, and confirm decisions with manager E-PIN.`}
      />

      {message ? <div className="banner success">{message}</div> : null}
      {error ? <div className="banner error">{error}</div> : null}

      <div className="tabs review-tabs">
        <button
          className={activeTab === "vendors" ? "chip chip-on" : "chip"}
          type="button"
          onClick={() => setActiveTab("vendors")}
        >{`Review ${tp("vendor")}`}</button>
        <button
          className={activeTab === "materials" ? "chip chip-on" : "chip"}
          type="button"
          onClick={() => setActiveTab("materials")}
        >{`Review ${tp("material")}`}</button>
      </div>

      {activeTab === "vendors" ? (
        <div className="section">
          <div className="filters-bar">
            <label>
              <span>Search {tp("vendor").toLowerCase()}</span>
              <input
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                placeholder={`Search ${t("vendor").toLowerCase()} name, email, or status`}
              />
            </label>
            <label>
              <span>Sort {tp("vendor").toLowerCase()}</span>
              <select
                value={vendorSort}
                onChange={(e) => setVendorSort(e.target.value)}
              >
                <option value="name">Name</option>
                <option value="status">Status</option>
                <option value="created">Created</option>
              </select>
            </label>
          </div>
          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--dense">
                <thead>
                  <tr>
                    <th>{t("vendor")}</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Decision Comment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorPager.pageData.map((item) => {
                    const state = vendorStatusMeta(
                      item.status ?? item.approved,
                    );
                    const approveDisabled = state.status === "approved" || state.status === "deleted";
                    const rejectDisabled = state.status === "rejected" || state.status === "deleted";
                    const primaryVendorLabel = state.status === "rejected" ? "Activate" : "Approve";
                    const showVendorReject = state.status === "approved";
                    const active = selectedVendor === item.name;
                    return (
                      <tr
                        key={item.name}
                        onClick={() => setSelectedVendor(item.name)}
                        style={
                          active
                            ? {
                                background: "var(--accent-soft)",
                                boxShadow: "inset 3px 0 0 var(--accent)",
                              }
                            : undefined
                        }
                      >
                        <td>{item.name}</td>
                        <td>{item.email || "—"}</td>
                        <td>
                          <span className={`status-pill ${state.cls}`}>
                            {state.label}
                          </span>
                        </td>
                        <td>{formatDateTime(item.created_at)}</td>
                        <td>{state.status === "rejected" ? (item.last_decision_comment || "—") : "—"}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="btn btn-primary"
                              type="button"
                              disabled={approveDisabled}
                              onClick={(e) => {
                                e.stopPropagation();
                                pinGate.askPinThen(() =>
                                  runVendorDecision(item.name, true),
                                );
                              }}
                            >
                              {primaryVendorLabel}
                            </button>
                            {showVendorReject ? (
                              <button
                                className="btn btn-secondary"
                                type="button"
                                disabled={rejectDisabled}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openVendorSuspendModal(item);
                                }}
                              >
                                Suspend
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleVendors.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        No {tp("vendor").toLowerCase()} awaiting review.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <Pager {...vendorPager} />
          </div>
        </div>
      ) : null}

      {activeTab === "materials" ? (
        <div className="section">
          <div className="page-header-row">
            <div>
              <h3>All {tp("material").toLowerCase()}</h3>
              <div className="helper-text">
                Review every submitted {t("material").toLowerCase()} across all{" "}
                {tp("vendor").toLowerCase()}.
              </div>
            </div>
          </div>

          <div className="filters-bar">
            <label>
              <span>Search {tp("material").toLowerCase()}</span>
              <input
                value={materialSearch}
                onChange={(e) => setMaterialSearch(e.target.value)}
                placeholder={`Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("type").toLowerCase()}, Std MRP, status, or created date`}
              />
            </label>
            <label>
              <span>Status</span>
              <select value={materialStatusFilter} onChange={(e) => setMaterialStatusFilter(e.target.value)}>
                {MATERIAL_STATUS_FILTERS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <DateRangePicker
              value={materialDateDraft}
              onChange={setMaterialDateDraft}
              onApply={() => setMaterialDateRange({ ...materialDateDraft })}
              onClear={(empty) => { setMaterialDateDraft(empty); setMaterialDateRange(empty); }}
            />
            <label>
              <span>Sort {tp("material").toLowerCase()}</span>
              <select
                value={materialSort}
                onChange={(e) => setMaterialSort(e.target.value)}
              >
                <option value="vendor">{t("vendor")}</option>
                <option value="material">{t("material")}</option>
                <option value="sku">SKU</option>
                <option value="type">{t("type")}</option>
                <option value="status">Status</option>
                <option value="created">Created</option>
              </select>
            </label>
          </div>

          <div className="helper-text" style={{ marginBottom: 10 }}>
            Click a {t("material").toLowerCase()} row to view the template.
          </div>
          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--dense">
                <thead>
                  <tr>
                    <th>{t("vendor")}</th>
                    <th>{t("material")}</th>
                    <th>SKU</th>
                    <th>{t("type")}</th>
                    <th>Std MRP Rs.</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Decision Comment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {materialPager.pageData.map((item, index) => {
                    const state = materialStatusMeta(
                      item.approval_status,
                      item.approved,
                    );
                    const rowKey = materialRowKey(item.vendor, item, index);
                    return (
                      <tr
                        key={rowKey}
                        className="clickable-row material-template-row"
                        tabIndex={0}
                        onClick={() => templateModal.openTemplate(item)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            templateModal.openTemplate(item);
                          }
                        }}
                      >
                        <td>{item.vendor || "—"}</td>
                        <td>
                          {item.material}
                          {item.predecessor_material_id ? (
                            <span className="status-pill status-pending ml-2">Replacement</span>
                          ) : null}
                        </td>
                        <td>{item.sku}</td>
                        <td>{item.type || "Others"}</td>
                        <td>{String(item.std_mrp_rs || "").trim() || "—"}</td>
                        <td>
                          <span className={`status-pill ${state.cls}`}>
                            {state.label}
                          </span>
                        </td>
                        <td>{formatDateTime(item.created_at)}</td>
                        <td>{item.last_decision_comment || "—"}</td>
                        <td>
                          <div className="table-actions">
                            {state.status === "pending" ? (
                              <>
                                <button className="btn btn-primary" type="button" onClick={(event) => { event.stopPropagation(); pinGate.askPinThen(() => runMaterialDecision(item, "approve")); }}>Approve</button>
                                <button className="btn btn-secondary" type="button" onClick={(event) => { event.stopPropagation(); openMaterialCommentModal(item, "send_back"); }}>Revert</button>
                              </>
                            ) : null}
                            {state.status === "approved" ? (
                              <>
                                <button className="btn btn-secondary" type="button" onClick={(event) => { event.stopPropagation(); openMaterialCommentModal(item, "reject"); }}>Reject</button>
                                <button className="btn btn-secondary" type="button" onClick={(event) => { event.stopPropagation(); openMaterialCommentModal(item, "withdraw"); }}>Withdraw</button>
                              </>
                            ) : null}
                            {state.status === "rejected" ? (
                              <>
                                <button className="btn btn-primary" type="button" onClick={(event) => { event.stopPropagation(); pinGate.askPinThen(() => runMaterialDecision(item, "activate")); }}>Activate</button>
                                <button className="btn btn-secondary" type="button" onClick={(event) => { event.stopPropagation(); openMaterialCommentModal(item, "withdraw"); }}>Withdraw</button>
                              </>
                            ) : null}
                            {state.status === "sent_back" ? <span className="helper-text">Awaiting Admin Resubmission</span> : null}
                            {state.status === "withdrawn" ? <span className="helper-text">When: {formatDateTime(item.updated_at)}</span> : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {materialRows.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        No {tp("material").toLowerCase()} found for this
                        workspace.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <Pager {...materialPager} />
          </div>
        </div>
      ) : null}

      <ModalShell
        open={materialModal.open}
        onClose={() => setMaterialModal({ open: false, action: "", item: null, comments: "" })}
        title={`${materialCommentActionLabel(materialModal.action)} material`}
        subtitle="Comments are required and will be retained in the audit trail."
        actions={(
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setMaterialModal({ open: false, action: "", item: null, comments: "" })}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              disabled={!String(materialModal.comments || "").trim()}
              onClick={submitMaterialCommentModal}
            >
              Continue
            </button>
          </>
        )}
      >
        <label>
          Comments
          <textarea
            rows={5}
            value={materialModal.comments}
            onChange={(event) =>
              setMaterialModal((current) => ({ ...current, comments: event.target.value }))
            }
          />
        </label>
      </ModalShell>


      <ModalShell
        open={vendorModal.open}
        onClose={() => setVendorModal({ open: false, item: null, comments: "" })}
        title={`Suspend ${t("vendor").toLowerCase()}`}
        subtitle="Comments are required and will be retained in the audit trail."
        actions={(
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setVendorModal({ open: false, item: null, comments: "" })}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              disabled={!String(vendorModal.comments || "").trim()}
              onClick={submitVendorCommentModal}
            >
              Continue
            </button>
          </>
        )}
      >
        <label>
          Comments
          <textarea
            rows={5}
            value={vendorModal.comments}
            onChange={(event) =>
              setVendorModal((current) => ({ ...current, comments: event.target.value }))
            }
          />
        </label>
      </ModalShell>

      <MaterialTemplateModal state={templateModal.state} onClose={templateModal.closeTemplate} />
      <PinModal
        uid={user?.uid || ""}
        open={pinGate.pinOpen}
        onClose={pinGate.closePin}
        onVerified={pinGate.handlePinVerified}
      />
    </div>
  );
}
